import { Check, ChevronDown } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface DropdownOption<T extends string> {
  id: T
  label: ReactNode
  hint?: ReactNode
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  className = '',
  align = 'left',
  up = false,
}: {
  value: T
  options: DropdownOption<T>[]
  onChange: (v: T) => void
  className?: string
  align?: 'left' | 'right'
  up?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ left: number; width: number; maxHeight: number; top?: number; bottom?: number } | null>(null)
  const selected = options.find((o) => o.id === value)

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const anchor = ref.current
      if (!anchor) return
      const r = anchor.getBoundingClientRect()
      const gap = 4
      const margin = 8
      const width = Math.min(360, Math.max(280, r.width))
      let left = align === 'right' ? r.right - width : r.left
      left = Math.min(Math.max(margin, left), window.innerWidth - width - margin)
      const spaceBelow = window.innerHeight - r.bottom - gap - margin
      const spaceAbove = r.top - gap - margin
      const openUp = up || (spaceBelow < 240 && spaceAbove > spaceBelow)
      const maxHeight = Math.max(160, openUp ? spaceAbove : spaceBelow)
      setBox(
        openUp
          ? { left, width, maxHeight, bottom: window.innerHeight - r.top + gap }
          : { left, width, maxHeight, top: r.bottom + gap },
      )
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, align, up, options.length])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" className="btn-secondary h-8 gap-1.5 px-2.5" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <span>{selected?.label ?? value}</span>
        <ChevronDown className="h-3.5 w-3.5 text-ink-muted" />
      </button>
      {open &&
        box &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            className="fixed z-[200] overflow-y-auto overscroll-contain rounded-[10px] border border-line bg-surface-raised py-1 shadow-token-lg"
            style={{ left: box.left, width: box.width, maxHeight: box.maxHeight, top: box.top, bottom: box.bottom }}
          >
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={opt.id === value}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-[13px] text-ink hover:bg-surface-hover"
                onClick={() => {
                  onChange(opt.id)
                  setOpen(false)
                }}
              >
                <span>
                  {opt.label}
                  {opt.hint && <span className="block text-[11px] text-ink-muted">{opt.hint}</span>}
                </span>
                {opt.id === value && <Check className="h-3.5 w-3.5 shrink-0 text-brand" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}

/** A button that opens a list of actions (no selected value). */
export function Menu({
  label,
  items,
  className = '',
}: {
  label: ReactNode
  items: { id: string; label: ReactNode; hint?: ReactNode; onSelect: () => void }[]
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" className="btn-ghost !h-7 gap-1 !text-[12px]" onClick={() => setOpen((v) => !v)}>
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-1 w-72 overflow-hidden rounded-[10px] border border-line bg-surface-raised py-1 shadow-token-lg">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="block w-full px-3 py-2 text-left hover:bg-surface-hover"
              onClick={() => {
                it.onSelect()
                setOpen(false)
              }}
            >
              <span className="block text-[13px] text-ink">{it.label}</span>
              {it.hint && <span className="block text-[11.5px] text-ink-muted">{it.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
